<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { IconCompare } from '@/components/design/icons';
import type {
  VaultFileRevisionDiff,
  VaultFileRevisionDiffPage,
  VaultFileRevisionInDb,
} from '@/api';
import { useVaultStore } from '@/stores/vaultStore';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaTooltip from '@/components/design/base/GaiaTooltip.vue';
import { gaiaAccent, gaiaNeutral } from '@/components/design/themes/tokens';
import VaultFileRevisionComparePane from '@/components/file_vault/VaultFileRevisionComparePane.vue';
import VaultFileRevisionDiffPanel from '@/components/file_vault/VaultFileRevisionDiffPanel.vue';
import { extractErrorDetail } from '@/utils/errorHandler';

// ─── Exported types (used by VaultFileRevisionDiffPanel) ─────────────────────

export type DiffLineType = 'added' | 'removed' | 'context' | 'page';

export interface DiffLine {
  text: string;
  type: DiffLineType;
  pageNumber?: number;
  pagePrefix?: '+' | '-' | ' ';
}

// ─── Props / emits ────────────────────────────────────────────────────────────

const props = defineProps<{
  show: boolean;
  fileUid: string;
  revisionA: VaultFileRevisionInDb | null;
  revisionB: VaultFileRevisionInDb | null;
}>();

const emit = defineEmits<{
  'update:show': [boolean];
}>();

// ─── State ────────────────────────────────────────────────────────────────────

const vaultStore = useVaultStore();

const diffData = ref<VaultFileRevisionDiff | null>(null);
const loadingDiff = ref(false);
const diffError = ref('');

type LayoutMode = 'columns' | 'stacked';
const layoutMode = ref<LayoutMode>('columns');

/** The two reading modes, as the header's icon-only segmented track draws them. */
const LAYOUT_OPTIONS: { value: LayoutMode; label: string; icon: string }[] = [
  {
    value: 'columns',
    label: 'Side by side',
    icon: 'M2.5 3h3.4v10H2.5zM6.9 3h2.2v10H6.9zM10.1 3h3.4v10h-3.4z',
  },
  { value: 'stacked', label: 'Diff on top', icon: 'M2.5 3h11v4h-11zM2.5 9h5v4h-5zM8.5 9h5v4h-5z' },
];

const paneA = ref<InstanceType<typeof VaultFileRevisionComparePane>>();
const paneB = ref<InstanceType<typeof VaultFileRevisionComparePane>>();

let resizeTimer: ReturnType<typeof setTimeout> | null = null;
function handleSplitResize(): void {
  if (resizeTimer) clearTimeout(resizeTimer);
  resizeTimer = setTimeout(() => {
    paneA.value?.reloadPDF();
    paneB.value?.reloadPDF();
  }, 150);
}

// ─── Data loading ─────────────────────────────────────────────────────────────

function watchShow(): boolean {
  return props.show;
}
function watchRevA(): VaultFileRevisionInDb | null {
  return props.revisionA;
}
function watchRevB(): VaultFileRevisionInDb | null {
  return props.revisionB;
}

watch([watchShow, watchRevA, watchRevB], async (): Promise<void> => {
  if (!props.show || !props.revisionA || !props.revisionB) return;
  loadingDiff.value = true;
  diffError.value = '';
  diffData.value = null;
  try {
    diffData.value = await vaultStore.getRevisionDiff(
      props.fileUid,
      props.revisionB.uid,
      props.revisionA.uid
    );
  } catch (error) {
    // A generated document that no longer renders answers 422 saying why.
    diffError.value = extractErrorDetail(error, 'Failed to load diff.');
  } finally {
    loadingDiff.value = false;
  }
});

// ─── Diff line transform ──────────────────────────────────────────────────────

function toFlatLines(pages: VaultFileRevisionDiffPage[]): DiffLine[] {
  const result: DiffLine[] = [];
  for (const page of pages) {
    // Infer whether this page is new, removed, or modified from its line statuses
    const hasAdded = page.lines.some(l => l.status === 'added');
    const hasRemoved = page.lines.some(l => l.status === 'removed');
    const hasUnchanged = page.lines.some(l => l.status === 'unchanged');
    let pagePrefix: '+' | '-' | ' ' = ' ';
    if (hasAdded && !hasRemoved && !hasUnchanged) pagePrefix = '+';
    else if (hasRemoved && !hasAdded && !hasUnchanged) pagePrefix = '-';

    result.push({ text: '', type: 'page', pageNumber: page.page, pagePrefix });

    for (const line of page.lines) {
      const type: DiffLineType =
        line.status === 'added' ? 'added' : line.status === 'removed' ? 'removed' : 'context';
      result.push({ text: line.text, type });
    }
  }
  return result;
}

const parsedLines = computed<DiffLine[]>(() => {
  if (!diffData.value?.pages.length) return [];
  return toFlatLines(diffData.value.pages);
});

// ─── Per-page highlight map ───────────────────────────────────────────────────

function getOrCreatePageEntry(
  map: Map<number, { removed: string[]; added: string[] }>,
  page: number
): { removed: string[]; added: string[] } {
  let entry = map.get(page);
  if (!entry) {
    entry = { removed: [], added: [] };
    map.set(page, entry);
  }
  return entry;
}

const changesByPage = computed(() => {
  const map = new Map<number, { removed: string[]; added: string[] }>();
  let curPage: number | null = null;
  for (const line of parsedLines.value) {
    if (line.type === 'page') {
      curPage = line.pageNumber ?? null;
      if (curPage !== null) getOrCreatePageEntry(map, curPage);
    } else if (curPage !== null) {
      const text = line.text.trim();
      if (!text) continue;
      const entry = getOrCreatePageEntry(map, curPage);
      if (line.type === 'removed') entry.removed.push(text);
      else if (line.type === 'added') entry.added.push(text);
    }
  }
  return map;
});

// Apply highlights for the given page number to each PDF
function applyPageHighlightsA(page: number): void {
  const changes = changesByPage.value.get(page);
  paneA.value?.setHighlightTextDirect(changes?.removed ?? []);
}

function applyPageHighlightsB(page: number): void {
  const changes = changesByPage.value.get(page);
  paneB.value?.setHighlightTextDirect(changes?.added ?? []);
}

// Seed page 1 highlights when diff data first arrives
watch(parsedLines, lines => {
  if (!lines.length) return;
  applyPageHighlightsA(1);
  applyPageHighlightsB(1);
});

// ─── Page navigation ──────────────────────────────────────────────────────────

function navigateToPage(line: DiffLine): void {
  const n = line.pageNumber;
  if (!n) return;
  paneA.value?.goToPage(n);
  paneB.value?.goToPage(n);
  // goToPage triggers pageChange events which call applyPageHighlightsA/B
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * The diff panel appears once per layout, so its eight props are bound once
 * here rather than spelled out twice and drifting apart. The attribution is the
 * NEWER revision's: the diff describes how B came to differ from A.
 */
const diffPanelProps = computed(() => ({
  parsedLines: parsedLines.value,
  addedLines: diffData.value?.added_lines ?? 0,
  removedLines: diffData.value?.removed_lines ?? 0,
  loading: loadingDiff.value,
  error: diffError.value,
  uploadedAt: props.revisionB?.created_at ?? null,
  changeDescription: props.revisionB?.change_description ?? null,
  uploaderEmail: props.revisionB?.created_by_user?.email ?? null,
}));

function handleClose(): void {
  emit('update:show', false);
}

const surface = gaiaNeutral.surface;
const barBorder = gaiaNeutral.borderDivider;
const accentColor = gaiaAccent.base;
const toggleBorder = gaiaNeutral.border;
const toggleIdle = gaiaNeutral.textTertiary;
const toggleHover = gaiaNeutral.surfaceHover;
const toggleActiveBg = gaiaNeutral.ink;
const toggleActiveFg = gaiaNeutral.surface;
</script>

<template>
  <!-- A full-bleed workspace, not a dialog: two documents and their differences
       need the whole window, and nothing behind it is actionable while it is
       open. Teleported so the app's own layout can't clip it. -->
  <teleport to="body">
    <div v-if="props.show" class="compare">
      <div class="bar">
        <n-icon :component="IconCompare" :size="16" :color="accentColor" />
        <gaia-text size="body" weight="bold" tone="ink" :style="{ flex: 'none' }">
          v{{ props.revisionA?.version_number }} → v{{ props.revisionB?.version_number }}
        </gaia-text>
        <gaia-text truncate size="row" tone="subtle">{{ props.revisionB?.file_name }}</gaia-text>

        <n-flex
          align="center"
          :size="10"
          :wrap="false"
          :style="{ marginLeft: 'auto', flex: 'none' }"
        >
          <!-- Two ways to read the same three panes. Icon-only: the labels are
               long ("Side by side" / "Diff on top") and this bar also carries a
               filename that must keep room to truncate into. -->
          <div class="layout-toggle">
            <gaia-tooltip v-for="option in LAYOUT_OPTIONS" :key="option.value" :delay="400">
              <template #trigger>
                <button
                  type="button"
                  class="layout-option"
                  :class="{ active: layoutMode === option.value }"
                  :aria-pressed="layoutMode === option.value"
                  :aria-label="option.label"
                  @click="layoutMode = option.value"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                  >
                    <path :d="option.icon" />
                  </svg>
                </button>
              </template>
              {{ option.label }}
            </gaia-tooltip>
          </div>

          <gaia-button quaternary size="small" @click="handleClose">
            <template #icon>
              <svg
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.4"
                stroke-linecap="round"
              >
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </template>
            Close
          </gaia-button>
        </n-flex>
      </div>

      <!-- Side by side: older | differences | newer, so a line in the middle
           has its two documents on either side of it. -->
      <n-split
        v-if="layoutMode === 'columns'"
        class="stage"
        :default-size="0.33"
        :min="0.15"
        :max="0.6"
        @update:size="handleSplitResize"
      >
        <template #1>
          <vault-file-revision-compare-pane
            ref="paneA"
            side="a"
            :file-uid="props.fileUid"
            :revision="props.revisionA"
            @loaded="applyPageHighlightsA(1)"
            @page-change="applyPageHighlightsA"
          />
        </template>
        <template #2>
          <n-split
            :default-size="0.5"
            :min="0.2"
            :max="0.75"
            :style="{ height: '100%' }"
            @update:size="handleSplitResize"
          >
            <template #1>
              <vault-file-revision-diff-panel
                v-bind="diffPanelProps"
                @navigate-page="navigateToPage"
              />
            </template>
            <template #2>
              <vault-file-revision-compare-pane
                ref="paneB"
                side="b"
                :file-uid="props.fileUid"
                :revision="props.revisionB"
                @loaded="applyPageHighlightsB(1)"
                @page-change="applyPageHighlightsB"
              />
            </template>
          </n-split>
        </template>
      </n-split>

      <!-- Diff on top: the full width for the text, with both documents beneath
           it — for reading the change list rather than the pages. -->
      <n-split
        v-else
        class="stage"
        direction="vertical"
        :default-size="0.38"
        :min="0.2"
        :max="0.75"
        @update:size="handleSplitResize"
      >
        <template #1>
          <vault-file-revision-diff-panel v-bind="diffPanelProps" @navigate-page="navigateToPage" />
        </template>
        <template #2>
          <n-split
            :default-size="0.5"
            :min="0.2"
            :max="0.8"
            :style="{ height: '100%' }"
            @update:size="handleSplitResize"
          >
            <template #1>
              <vault-file-revision-compare-pane
                ref="paneA"
                side="a"
                :file-uid="props.fileUid"
                :revision="props.revisionA"
                @loaded="applyPageHighlightsA(1)"
                @page-change="applyPageHighlightsA"
              />
            </template>
            <template #2>
              <vault-file-revision-compare-pane
                ref="paneB"
                side="b"
                :file-uid="props.fileUid"
                :revision="props.revisionB"
                @loaded="applyPageHighlightsB(1)"
                @page-change="applyPageHighlightsB"
              />
            </template>
          </n-split>
        </template>
      </n-split>
    </div>
  </teleport>
</template>

<style scoped>
.compare {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  background: v-bind(surface);
}

.bar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: none;
  height: 48px;
  padding: 0 16px;
  border-bottom: 1px solid v-bind(barBorder);
}

.stage {
  flex: 1;
  min-height: 0;
}

/* One bordered track with the segments butted together, so the active ink fill
   reaches the frame rather than floating inside it. */
.layout-toggle {
  display: flex;
  flex: none;
  overflow: hidden;
  border: 1px solid v-bind(toggleBorder);
  border-radius: 9px;
}

.layout-option {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 30px;
  padding: 0;
  border: none;
  background: v-bind(surface);
  color: v-bind(toggleIdle);
  cursor: pointer;
}

.layout-option:hover {
  background: v-bind(toggleHover);
}

.layout-option.active {
  background: v-bind(toggleActiveBg);
  color: v-bind(toggleActiveFg);
}

/* naive draws the split handle as a hairline; the panes each own a border, so
   the default 3px band would read as a second, thicker rule between them. */
.stage :deep(.n-split-pane-divider) {
  background: v-bind(barBorder);
}
</style>
